import { useState } from 'react'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import LinearProgress from '@mui/material/LinearProgress'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'react-toastify'

import type { ChargeKind, PaymentMethod, SubscriptionStatus } from '@server/domain/billing'

import CustomAvatar from '@core/components/mui/avatar'
import CustomChip from '@core/components/mui/chip'
import type { ThemeColor } from '@core/types'
import { ModalConfirm } from '@components/modal-confirm'
import ExtraQuotaDialog from '@components/due-diligence/extra-quota-dialog'

import { useTRPC } from '@/lib/trpc'
import { formatBrl, formatDate, percentOf } from '@/lib/due-diligence'

export const Route = createFileRoute('/_dashboard/account')({
  component: MySubscription
})

const SUBSCRIPTION_STATUS: Record<SubscriptionStatus, { label: string; color: ThemeColor }> = {
  active: { label: 'Ativa', color: 'success' },
  cancelingAtCycleEnd: { label: 'Cancela no fim do ciclo', color: 'warning' },
  suspended: { label: 'Suspensa', color: 'error' },
  trial: { label: 'Período de teste', color: 'info' }
}

const CHARGE_STATUS: Record<string, { label: string; color: ThemeColor }> = {
  paid: { label: 'Pago', color: 'success' },
  pending: { label: 'Pendente', color: 'warning' },
  failed: { label: 'Falhou', color: 'error' },
  canceled: { label: 'Cancelado', color: 'secondary' },
  expired: { label: 'Expirado', color: 'secondary' }
}

const CHARGE_KIND: Record<ChargeKind, string> = {
  subscription: 'Mensalidade do plano',
  extra: 'Dossiê extra avulso',
  upgrade: 'Upgrade de plano'
}

const METHOD_ICON: Record<PaymentMethod, string> = {
  card: 'tabler-credit-card',
  pix: 'tabler-qrcode',
  boleto: 'tabler-file-invoice'
}

const METHOD_LABEL: Record<PaymentMethod, string> = { card: 'Cartão', pix: 'Pix', boleto: 'Boleto' }

/** A usage bar with the "getting close to the limit" warning built in. */
const QuotaBar = ({ label, used, limit }: { label: string; used: number; limit: number }) => {
  const unlimited = limit === -1
  const percent = unlimited ? 0 : percentOf(used, limit)
  const color: ThemeColor = percent >= 100 ? 'error' : percent >= 80 ? 'warning' : 'primary'

  return (
    <div>
      <div className='flex items-baseline justify-between gap-2 mbe-2'>
        <Typography variant='body2' color='text.secondary'>
          {label}
        </Typography>
        <Typography variant='body2' className='font-medium'>
          {used} / {unlimited ? '∞' : limit}
          {!unlimited && limit > 0 && (
            <Typography component='span' variant='caption' color='text.disabled'>
              {' '}
              · {percent}%
            </Typography>
          )}
        </Typography>
      </div>
      <LinearProgress
        variant='determinate'
        value={unlimited ? 100 : Math.min(percent, 100)}
        color={unlimited ? 'primary' : color}
        className='bs-[8px] rounded-full'
      />
      {!unlimited && percent >= 80 && percent < 100 && (
        <Typography variant='caption' color='warning.tonalText' className='flex items-center gap-1 mbs-1'>
          <i className='tabler-alert-triangle text-sm' /> Perto do limite mensal
        </Typography>
      )}
    </div>
  )
}

function MySubscription() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [extraOpen, setExtraOpen] = useState(false)

  const { data, isPending } = useQuery(trpc.billing.mySubscription.queryOptions())
  const { data: charges = [] } = useQuery(trpc.billing.charges.queryOptions({ limit: 10 }))

  const cancel = useMutation(
    trpc.billing.cancel.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.billing.mySubscription.queryKey() })
        toast.success('Assinatura cancelada. O acesso vale até o fim do ciclo.')
      },
      onError: error => toast.error(error.message)
    })
  )

  if (isPending) {
    return (
      <Grid container spacing={6}>
        <Grid size={12}>
          <Skeleton variant='rounded' height={200} />
        </Grid>
        <Grid size={12}>
          <Skeleton variant='rounded' height={180} />
        </Grid>
      </Grid>
    )
  }

  if (!data) {
    return (
      <Card>
        <CardContent className='flex flex-col items-center gap-4 plb-16 text-center'>
          <CustomAvatar skin='light' color='primary' variant='rounded' size={52}>
            <i className='tabler-sparkles text-[28px]' />
          </CustomAvatar>
          <div>
            <Typography variant='h5'>Você ainda não tem um plano ativo</Typography>
            <Typography color='text.secondary' className='max-is-[460px]'>
              Escolha um plano para usar análises automáticas, relatórios em PDF e dossiês completos.
            </Typography>
          </div>
          <Button
            variant='contained'
            startIcon={<i className='tabler-sparkles' />}
            onClick={() => navigate({ to: '/plans' })}
          >
            Ver planos
          </Button>
        </CardContent>
      </Card>
    )
  }

  const { subscription, plan, usage } = data
  const statusInfo = SUBSCRIPTION_STATUS[subscription.status]

  // The ceiling is what the plan gives plus whatever extras this cycle bought.
  const unlimited = plan?.monthlyAnalyses === -1
  const analysesLimit = unlimited ? -1 : (plan?.monthlyAnalyses ?? 0) + usage.extraDossiers
  const dossiersLimit = unlimited ? -1 : (plan?.monthlyDossiers ?? 0) + usage.extraDossiers

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={12}>
          <Card
            sx={{
              background:
                'linear-gradient(135deg, var(--mui-palette-primary-main) 0%, var(--mui-palette-primary-dark) 100%)',
              color: 'common.white'
            }}
          >
            <CardContent className='flex flex-wrap items-end justify-between gap-6'>
              <div>
                <div className='flex items-center gap-2 mbe-2'>
                  <CustomChip
                    round='true'
                    size='small'
                    label={statusInfo.label}
                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'common.white' }}
                  />
                  <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Plano atual
                  </Typography>
                </div>
                <Typography variant='h4' sx={{ color: 'common.white' }}>
                  {plan?.name ?? subscription.planCode}
                </Typography>
                {plan && plan.monthlyPriceBrl > 0 && (
                  <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    {formatBrl(plan.monthlyPriceBrl)}/mês
                  </Typography>
                )}
                <Typography
                  variant='body2'
                  sx={{ color: 'rgba(255,255,255,0.75)' }}
                  className='flex items-center gap-1 mbs-3'
                >
                  <i className='tabler-calendar text-base' /> Próxima renovação em {formatDate(subscription.cycleEnd)}
                </Typography>
              </div>

              <div className='flex flex-wrap items-center gap-3'>
                <Button
                  variant='contained'
                  color='inherit'
                  endIcon={<i className='tabler-arrow-up-right' />}
                  sx={{
                    backgroundColor: 'common.white',
                    color: 'primary.main',
                    '&:hover': { backgroundColor: 'common.white' }
                  }}
                  onClick={() => navigate({ to: '/plans' })}
                >
                  Trocar de plano
                </Button>
                {subscription.status === 'active' && (
                  <ModalConfirm
                    variant={undefined}
                    title='Cancelar a assinatura?'
                    content='Você mantém o acesso até o fim do ciclo atual. Depois disso, as análises param.'
                    isPending={cancel.isPending}
                    onConfirm={() => cancel.mutate()}
                  >
                    <Button
                      variant='outlined'
                      color='inherit'
                      sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'common.white' }}
                    >
                      Cancelar assinatura
                    </Button>
                  </ModalConfirm>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card className='bs-full'>
            <CardHeader title='Consumo do ciclo atual' subheader='Zera na próxima renovação' />
            <Divider />
            <CardContent className='flex flex-col gap-6'>
              <QuotaBar label='Análises' used={usage.analysesUsed} limit={analysesLimit} />
              <QuotaBar label='Dossiês' used={usage.dossiersUsed} limit={dossiersLimit} />

              {usage.extraDossiers > 0 && (
                <div className='flex items-center gap-3 rounded bg-actionHover plb-3 pli-4'>
                  <i className='tabler-wallet text-primary text-xl' />
                  <Typography variant='body2' color='text.secondary'>
                    Extras comprados neste ciclo: <strong>{usage.extraDossiers}</strong>
                  </Typography>
                </div>
              )}

              <Button
                variant='tonal'
                color='primary'
                startIcon={<i className='tabler-bolt' />}
                onClick={() => setExtraOpen(true)}
              >
                Comprar dossiê extra
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card className='bs-full'>
            <CardHeader title='Cobranças recentes' subheader='Últimos lançamentos da conta' />
            <Divider />
            <CardContent>
              {charges.length === 0 ? (
                <Typography color='text.disabled' className='text-center plb-8'>
                  Nenhuma cobrança ainda.
                </Typography>
              ) : (
                <div className='flex flex-col'>
                  {charges.map((charge, index) => {
                    const info = CHARGE_STATUS[charge.status] ?? { label: charge.status, color: 'secondary' as ThemeColor }

                    return (
                      <div key={charge.id}>
                        <div className='flex items-center gap-4 plb-3'>
                          <CustomAvatar skin='light' color='secondary' variant='rounded' size={36}>
                            <i className={`${METHOD_ICON[charge.method]} text-[18px]`} />
                          </CustomAvatar>
                          <div className='flex-1 min-is-0'>
                            <Typography className='font-medium'>{CHARGE_KIND[charge.kind]}</Typography>
                            <Typography variant='caption' color='text.disabled'>
                              {formatDate(charge.createdAt)} · {METHOD_LABEL[charge.method]}
                            </Typography>
                          </div>
                          <Typography className='font-medium whitespace-nowrap'>{formatBrl(charge.amountBrl)}</Typography>
                          <CustomChip round='true' size='small' variant='tonal' color={info.color} label={info.label} />
                        </div>
                        {index < charges.length - 1 && <Divider />}
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ExtraQuotaDialog
        open={extraOpen}
        extraPriceBrl={plan?.extraDossierPriceBrl ?? 19}
        reason='Compre um dossiê avulso sem mexer no seu plano.'
        onClose={() => setExtraOpen(false)}
      />
    </>
  )
}
